/* eslint-disable react/no-unused-state */
/* eslint-disable react/static-property-placement */
/* global css, THEME */
// @ts-nocheck
import React from 'react';
import PropTypes from 'react-proptypes';
import { Box, Link, Typography } from '@mui/material';
import cx from 'classnames';
import Modal from '../shared/modal';
import { SaveIcon } from '../icons';
import SearchBox from '../shared/search-box';
import { AppContext } from '../hooks/use-app';
import { cssInJS } from '../utils/styles';
import ArcGISLoginButton from '../arcGIS/arcGIS-login-button';
import GroupdetailsMapLayersEmptyImage from '../images/groupdetails-map-layers-empty';
import Poster from '../components/poster';
import LayerPickerActiveLayersList from './layer-picker-active-layers-list';
import BASEMAPS, { ESRI_GOOGLE_TILE_TYPE } from './basemaps';
import type { BasemapOption } from './basemaps';
import LayerPickerAvailableLayersList from './layer-picker-available-layers-list';
import BasemapOptionComponent from './basemap-option';
import BasemapPicker from './basemap-picker';

export default class LayerPicker extends React.Component {
  static propTypes = {
    view: PropTypes.func,
    editing: PropTypes.bool,
    show: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    esriEnabled: PropTypes.bool.isRequired,
  };

  static contextType = AppContext;

  constructor(props, context) {
    super(props, context);

    this.state = {
      searchFilter: '',
      layersLastUpdated: context.layerSettings.lastUpdated,
    };

    this._previousBodyUserSelect = null;
    this._previousBodyWebkitUserSelect = null;
  }

  componentWillUnmount() {
    this.enableTextSelection();
  }

  render() {
    const { styles } = LayerPicker;
    const { view = () => null } = this.props;

    return (
      <Modal
        show={this.props.show}
        onHide={this.handlePickerHide}
        dialogTransitionTimeout={150}
        backdropTransitionTimeout={150}
        style={{ zIndex: '700', position: 'absolute' }}
      >
        <div
          data-testid="layer-picker-modal-content"
          className={cx('modal-content', styles.picker)}
          style={{ zIndex: '700' }}
        >
          {this.renderHeader()}
          {this.renderBasemapPicker(view())}
          {this.renderOverlayPicker()}
        </div>
      </Modal>
    );
  }

  renderHeader() {
    const { styles } = LayerPicker;

    return (
      <div className={css(styles.header, styles.topHeader)}>
        <div className={styles.headerLabel}>Select Basemap & Layers</div>
        <button
          className={styles.doneButton}
          title="Done"
          onClick={this.handleDoneClick}
          type="button"
        >
          <SaveIcon style={{ fill: '#7db400' }} />
        </button>
      </div>
    );
  }

  renderBasemapPicker() {
    const { styles } = LayerPicker;
    const { esriEnabled } = this.props;
    const style = styles.basemaps;

    if (esriEnabled) {
      return (
        <BasemapPicker
          activeBasemapId={this.activeBasemap ? this.activeBasemap.id : null}
          assets={this.context.globals.assets}
          onChange={this.handleBasemapClick}
        />
      );
    }

    return (
      <div className={style}>
        {this.renderBasemapPickerOptions()}
      </div>
    );
  }

  handleSearchChange = (value) => {
    this.setState({ searchFilter: value });
  };

  handleForceUpdate = () => {
    this.setState({ layersLastUpdated: new Date().getTime() });
  };

  basemapPreviewUrl(map: BasemapOption) {
    const { globals } = this.context;
    const { id, thumbnailUrl, googleThumbnail } = map;

    if (id === 'none') {
      // Empty image
      return "data:image/svg+xml;charset=utf8,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%3E%3C/svg%3E";
    }

    return this.props.esriEnabled
      ? thumbnailUrl
      : globals.assets[`${googleThumbnail}-preview.png`];
  }

  get activeBasemap() {
    return this.context.layerSettings.activeBasemap;
  }

  get activeGoogleBasemap() {
    return this.context.layerSettings.googleEngineActiveBasemap;
  }

  basemapTestId(map: BasemapOption) {
    const { id, esriMapTypeId, googleMapTypeId } = map;

    if (id === 'none') {
      return id;
    }

    return this.props.esriEnabled
      ? esriMapTypeId
      : googleMapTypeId;
  }

  basemapOptionComponent(map) {
    const handler = () => {
      this.handleBasemapClick(map);
    };
    const activeBasemapForEngine = this.props.esriEnabled ? this.activeBasemap : this.activeGoogleBasemap;
    const isChecked = (activeBasemapForEngine == null && map.id === 'none') ? true
      : activeBasemapForEngine.id === map.id;

    return (
      BasemapOptionComponent({
        map,
        isChecked,
        onClick: handler,
        testId: this.basemapTestId(map),
        previewUrl: this.basemapPreviewUrl(map),
      })
    );
  }

  renderBasemapPickerOptions() {
    return BASEMAPS.map((map) => {
      if (map.type === ESRI_GOOGLE_TILE_TYPE) {
        return <div key={`basemap-esri-google-tile-${map.name}`} />;
      }
      return this.basemapOptionComponent(map);
    });
  }

  hasNoVisibleLayers(layers) {
    const len = Boolean(layers.length);
    const { esriEnabled } = this.props;
    const visibleLayers = layers.filter((layer) => layer.type !== 'mmpk');
    const visibleLen = Boolean(visibleLayers.length);

    return !len
      || !visibleLen
      || (!esriEnabled && visibleLayers.every((layer) => layer.type === 'kml'));
  }

  renderOverlayPicker() {
    const { editing } = this.props;
    const { layerSettings: { layerList, arcgisLayerList } } = this.context;

    const arcgisLayers = Boolean(arcgisLayerList.length);

    if (!arcgisLayers && this.hasNoVisibleLayers(layerList)) {
      return this.renderEmptyOverlays();
    }

    const { styles } = LayerPicker;

    return (
      <div className={styles.overlays}>
        <div className={styles.available}>
          <div className={css(styles.header, styles.overlayHeaderLabel)}>
            Choose Layers
            {
              this.props.esriEnabled && (
                <ArcGISLoginButton editing={editing} allowRefresh onChange={this.handleForceUpdate} />
              )
            }
          </div>
          {this.renderSearch()}
          {this.renderAvailableLayers()}
        </div>
        <div className={styles.active}>
          <div className={css(styles.header, styles.overlayHeaderLabel)}>Order Layers</div>
          {this.renderActiveLayers()}
        </div>
      </div>
    );
  }

  renderEmptyOverlays() {
    const { role } = this.context;
    const emptyHeadingText = 'There are currently no custom layers.';

    return (
      <Box sx={{ textAlign: 'center', paddingTop: '42px', fontSize: '14px' }}>
        <Poster Image={GroupdetailsMapLayersEmptyImage} imageWidth="230px" heading={emptyHeadingText} />
        {role.canManageLayers
          && (
            <Typography variant="caption" color="#323232" fontSize="16px" lineHeight="30px">
              Go to
              <Link href="/layers" rel="noreferrer" target="_blank"> Map Layers </Link>
              to create one
              {
                this.props.esriEnabled && (
                  <Box> or </Box>
                )
              }
            </Typography>
          )}
        {
          this.props.esriEnabled && (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '8px' }}>
              <ArcGISLoginButton allowRefresh={false} onChange={this.handleForceUpdate} />
            </Box>
          )
        }
      </Box>
    );
  }

  renderSearch() {
    const { styles } = LayerPicker;

    return (
      <div className={styles.searchContainer}>
        <SearchBox
          value={this.state.searchFilter || ''}
          onChange={this.handleSearchChange}
          className={styles.search}
        />
      </div>
    );
  }

  renderAvailableLayers() {
    const layers = this.sortedAvailableLayers;
    const { layerSettings: { activeLayersImmutable } } = this.context;

    const { styles } = LayerPicker;

    return (
      <div className={css(styles.availableBox, styles.boxContainerLeft)}>
        <LayerPickerAvailableLayersList
          className={styles.listContainer}
          availableLayers={layers}
          activeLayers={activeLayersImmutable.toJS()}
          onClick={this.handleLayersClick}
          esriEnabled={this.props.esriEnabled}
        />
      </div>
    );
  }

  renderActiveLayers() {
    const { styles } = LayerPicker;

    const items = this.context.layerSettings.activeOverlays;

    return (
      <div className={styles.boxContainer}>
        <LayerPickerActiveLayersList
          className={styles.listContainer}
          items={items}
          filter=""
          onSortStart={this.handleLayerSortStart}
          onSortEnd={this.handleLayerSortEnd}
          lockAxis="y"
          axis="y"
          lockToContainerEdges
          useDragHandle
          helperClass={styles.dragHelper}
          handleLayersClick={this.handleLayersClick}
        />
      </div>
    );
  }

  disableTextSelection() {
    if (typeof document === 'undefined') {
      return;
    }

    this._previousBodyUserSelect ??= document.body.style.userSelect;
    this._previousBodyWebkitUserSelect ??= document.body.style.webkitUserSelect;

    document.body.style.userSelect = 'none';
    document.body.style.webkitUserSelect = 'none';
  }

  enableTextSelection() {
    if (typeof document === 'undefined') {
      return;
    }

    if (this._previousBodyUserSelect != null) {
      document.body.style.userSelect = this._previousBodyUserSelect;
      this._previousBodyUserSelect = null;
    }

    if (this._previousBodyWebkitUserSelect != null) {
      document.body.style.webkitUserSelect = this._previousBodyWebkitUserSelect;
      this._previousBodyWebkitUserSelect = null;
    }
  }

  get sortedAvailableLayers() {
    const { layerSettings } = this.context;
    const { searchFilter } = this.state;

    return layerSettings.layerList.filter((layer) => {
      if (!searchFilter) {
        return true;
      }

      return layer.name.toLowerCase().indexOf(searchFilter.toLowerCase()) > -1;
    }).sort((a, b) => {
      if (a.name.toLowerCase() < b.name.toLowerCase()) {
        return -1;
      }
      if (a.name.toLowerCase() > b.name.toLowerCase()) {
        return 1;
      }
      return 0;
    });
  }

  handleDoneClick = () => {
    this.props.onClose();
  };

  handlePickerHide = () => {
    this.props.onClose();
  };

  handleBasemapClick = (map) => {
    const { esriEnabled } = this.props;
    this.context.layerSettings.updateBasemap(map, esriEnabled);
    this.setState({ layersLastUpdated: this.context.layerSettings.lastUpdated });
  };

  handleLayersClick = (overlay) => {
    this.context.layerSettings.toggleOverlay(overlay);
    this.setState({ layersLastUpdated: this.context.layerSettings.lastUpdated });
  };

  handleLayerSortStart = () => {
    this.disableTextSelection();
  };

  handleLayerSortEnd = ({ oldIndex, newIndex }) => {
    this.enableTextSelection();

    if (oldIndex !== newIndex) {
      this.context.layerSettings.moveOverlay(oldIndex, newIndex);
      this.setState({ layersLastUpdated: this.context.layerSettings.lastUpdated });
    }
  };
}

const styles = cssInJS({
  container: {
  },

  picker: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: 'auto',
    width: 700,
    height: 700,
    maxHeight: 'calc(100vh - 40px)',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 3,
    overflow: 'hidden',
    boxShadow: '0 14px 45px rgba(0,0,0,0.25), 0 10px 18px rgba(0,0,0,0.22)',
    fontFamily: THEME.fontFamily,
    backgroundColor: '#fff',
  },

  topHeader: {
    zIndex: 2,
    height: 48,
  },

  header: {
    backgroundColor: '#fff',
    fontWeight: 'bold',
    fontSize: 18,
    borderBottom: '1px solid #d9d9d9',
    position: 'relative',
    display: 'flex',
  },

  headerLabel: {
    flex: '1 1 auto',
    padding: 12,
  },

  doneButton: {
    flex: '0 1 auto',
    width: 48,
    minWidth: 48,
    height: 47,
    padding: 8,
    cursor: 'pointer',
    border: 0,
    background: '#ffffff',
    borderLeft: '1px solid #d9d9d9',
  },

  basemaps: {
    display: 'flex',
    padding: 8,
    boxShadow: '0 -4px 14px 3px rgba(0,0,0,0.3)',
    position: 'relative',
    zIndex: 1,
    height: 180,
  },

  searchContainer: {
    height: 42,
    padding: '6px',
    borderBottom: '1px solid #eee',
    borderRight: '1px solid #eee',
  },

  search: {
    width: 436,
  },

  overlays: {
    display: 'flex',
    flex: 1,
    minHeight: 0,
  },

  available: {
    flex: '0 0 450px',
    width: 450,
    display: 'flex',
    flexDirection: 'column',
  },

  active: {
    flex: '0 0 250px',
    width: 250,
    display: 'flex',
    flexDirection: 'column',
  },

  overlayHeaderLabel: {
    padding: 12,
    height: 48,
    backgroundColor: '#f7f7f7',
  },

  availableBox: {
    borderRight: '1px solid #eee',
  },

  boxContainerLeft: {
    flex: 1,
    minHeight: 0,
  },

  boxContainer: {
    flex: 1,
    minHeight: 0,
  },

  listContainer: {
    overflowY: 'auto',
    height: '100%',
    padding: 0,
    paddingTop: 0,
    paddingBottom: 0,
    boxSizing: 'border-box',
  },

  dragHelper: {
    borderRadius: 2,
    zIndex: 99999,
    boxShadow: '0 -4px 14px 3px rgba(0, 0, 0, 0.1)',
  },
});

LayerPicker.styles = styles;
